<div class="modal-header">
  <h5 class="modal-title"><i class="fas fa-flask"></i> &nbsp;<ng-container i18n>Set Booking Formula</ng-container></h5>
  <button (click)="close()" type="button" class="close">
    <i class="far fa-times"></i>
  </button>
</div>
<div class="modal-body">
  <form [formGroup]="form">
    <div class="row">
      <div class="col-sm-6">
        <mat-form-field2 class="mat-block">
          <mat-select
            formControlName="base"
            placeholder="Start the formula with:"
            i18n-placeholder
            [disabled]="!isBaseTypeCustomizable"
            required
          >
            <mat-option [value]="item.value" *ngFor="let item of baseTypes">{{ item.label }}</mat-option>
          </mat-select>
          <mat-error *ngIf="form.get('base').touched && form.get('base').hasError('required')">
            <ng-container i18n>This is required</ng-container>
          </mat-error>
        </mat-form-field2>
      </div>
      <div class="col-sm-6" *ngIf="!data.channel">
        <mat-form-field2 class="mat-block">
          <app-select
            [items]="channels"
            formControlName="channel_id"
            bindValue="id"
            bindLabel="name"
            placeholder="Channel"
            i18n-placeholder
            matTooltipPosition="above"
            [required]="true"
          >
          </app-select>
          <mat-error *ngIf="form.get('channel_id').touched && form.get('channel_id').hasError('required')">
            <ng-container i18n>Channel is required</ng-container>
          </mat-error>
        </mat-form-field2>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-6">
        <mat-form-field2 class="mat-block">
          <input type="text" formControlName="name" matInput required placeholder="Formula Name" i18n-placeholder />
          <mat-error *ngIf="form.get('name').touched && form.get('name').hasError('required')">
            <ng-container i18n>Formula Name is required</ng-container>
          </mat-error>
        </mat-form-field2>
      </div>
      <div class="col-sm-3">
        <app-form-switch (valueChange)="onToggleStatus($event)" formControlName="status" class="status-toggle" i18n>
          Status
        </app-form-switch>
      </div>
      <div class="col-sm-3">
        <div class="col-sm-6">
          <mat-checkbox
            formControlName="cumulative"
            matTooltip="This function allows you to change the order in which the line items are deducted."
            i18n-matTooltip
            class="cumulative-toggle"
          >
            <ng-container i18n *ngIf="form.get('cumulative').value">Cumulative</ng-container>
            <ng-container i18n *ngIf="!form.get('cumulative').value">Aggregate</ng-container>
          </mat-checkbox>
        </div>
      </div>
      <div class="col-sm-6">
        <app-rental-select
          [ctrl]="form.get('rentals')"
          placeholder="Rentals"
          i18n-placeholder
          [inline]="false"
          [matTooltipClass]="rentalIds.length > 20 ? 'text-center tooltip-lg' : 'text-center'"
          [matTooltip]="getRentalsView(rentalIds)"
        >
        </app-rental-select>
      </div>

      <!-- Formula Validity Criteria -->
      <div class="col-sm-3">
        <mat-form-field2
          class="mat-block"
          matTooltip="Select which booking date to use when matching this formula."
          i18n-matTooltip
        >
          <mat-select formControlName="validity_criteria" placeholder="Validity Criteria" i18n-placeholder>
            <mat-option [value]="item.value" *ngFor="let item of validityCriteriaOptions">{{ item.label }}</mat-option>
          </mat-select>
        </mat-form-field2>
      </div>

      <!-- Range Type toggle -->
      <div class="col-sm-3">
        <mat-form-field2
          class="mat-block"
          matTooltip="Choose whether to restrict this formula by month range or a specific date range."
          i18n-matTooltip
        >
          <mat-select
            formControlName="range_type"
            placeholder="Range Type"
            i18n-placeholder
          >
            <mat-option [value]="item.value" *ngFor="let item of rangeTypeOptions">{{ item.label }}</mat-option>
          </mat-select>
        </mat-form-field2>
      </div>

      <!-- Month Range (default / legacy) -->
      <ng-container *ngIf="!isDateRange">
        <div class="col-sm-3">
          <mat-form-field2
            class="mat-block"
            matTooltip="This indicates the time from which month the formula will be applied to a booking."
            i18n-matTooltip
          >
            <mat-select formControlName="start_month" placeholder="Start Month" i18n-placeholder required>
              <mat-option [value]="item.value" *ngFor="let item of months">{{ item.label }}</mat-option>
            </mat-select>
            <mat-error *ngIf="form.get('start_month').touched && form.get('start_month').hasError('required')">
              <ng-container i18n>Start month is required</ng-container>
            </mat-error>
            <mat-error i18n *ngIf="form.get('start_month').hasError('max')">
              Start month should not greater than end month
            </mat-error>
            <mat-error i18n *ngIf="form.get('start_month').hasError('overlap')">
              Date range is overlapping with other formulas.
            </mat-error>
          </mat-form-field2>
        </div>
        <div class="col-sm-3">
          <mat-form-field2
            class="mat-block"
            matTooltip="This indicates the time until which month the formula will be applied to a booking."
            i18n-matTooltip
          >
            <mat-select formControlName="end_month" placeholder="End Month" i18n-placeholder required>
              <mat-option [value]="item.value" *ngFor="let item of months">{{ item.label }}</mat-option>
            </mat-select>
            <mat-error *ngIf="form.get('end_month').touched && form.get('end_month').hasError('required')">
              <ng-container i18n>End month is required</ng-container>
            </mat-error>
            <mat-error i18n *ngIf="form.get('end_month').hasError('min')">
              End month should not less than start month
            </mat-error>
          </mat-form-field2>
        </div>
      </ng-container>

      <!-- Date Range picker (when range_type = 'date') -->
      <ng-container *ngIf="isDateRange">
        <div class="col-sm-3">
          <mat-form-field2
            class="mat-block"
            matTooltip="The formula will be applied to bookings on or after this date."
            i18n-matTooltip
          >
            <input
              matInput
              [matDatepicker]="startDatePicker"
              formControlName="start_date"
              placeholder="Start Date"
              i18n-placeholder
              [min]="minDate"
              [max]="form.get('end_date').value || maxDate"
              (focus)="startDatePicker.open()"
              readonly
              required
            />
            <i matSuffix (click)="startDatePicker.open()" class="far fa-calendar-alt"></i>
            <mat-datepicker #startDatePicker></mat-datepicker>
            <mat-error *ngIf="form.get('start_date').touched && form.get('start_date').hasError('required')">
              <ng-container i18n>Start date is required</ng-container>
            </mat-error>
            <mat-error i18n *ngIf="form.get('start_date').hasError('overlap')">
              Date range is overlapping with other formulas.
            </mat-error>
          </mat-form-field2>
        </div>
        <div class="col-sm-3">
          <mat-form-field2
            class="mat-block"
            matTooltip="The formula will be applied to bookings on or before this date."
            i18n-matTooltip
          >
            <input
              matInput
              [matDatepicker]="endDatePicker"
              formControlName="end_date"
              placeholder="End Date"
              i18n-placeholder
              [min]="form.get('start_date').value || minDate"
              [max]="maxDate"
              (focus)="endDatePicker.open()"
              readonly
              required
            />
            <i matSuffix (click)="endDatePicker.open()" class="far fa-calendar-alt"></i>
            <mat-datepicker #endDatePicker></mat-datepicker>
            <mat-error *ngIf="form.get('end_date').touched && form.get('end_date').hasError('required')">
              <ng-container i18n>End date is required</ng-container>
            </mat-error>
          </mat-form-field2>
        </div>
      </ng-container>
    </div>

    <div
      formArrayName="formula"
      *ngFor="let item of formulaItems.controls; let i = index"
      class="bg-default p-3 rounded pl-4 position-relative mb-3"
    >
      <div [formGroupName]="i" class="d-flex gap-3">
        <button
          (click)="removeFormula(i)"
          class="btn btn-secondary-danger btn-circle position-absolute"
          type="button"
          style="left: -1rem; top: 2.5rem"
        >
          <i class="cs-icon cs-icon-trash"></i>
        </button>
        <mat-form-field2 class="mat-block">
          <input type="text" formControlName="name" matInput required placeholder="Item Name" i18n-placeholder />
          <mat-error *ngIf="item['controls'].name.touched && item.hasError('required', ['name'])">
            <ng-container i18n>Item Name is required</ng-container>
          </mat-error>
        </mat-form-field2>
        <mat-form-field2 class="mat-block">
          <mat-label i18n>Amount</mat-label>
          <input type="number" min="0" formControlName="amount" required matInput />
          <mat-error *ngIf="item['controls'].amount.touched && item.hasError('required', ['amount'])">
            <ng-container i18n>Amount is required</ng-container>
          </mat-error>
          <mat-error *ngIf="item['controls'].amount.touched && item.hasError('percent', ['amount'])">
            <ng-container i18n>Amount should be greater than</ng-container> <b>0</b>
            <ng-container i18n>and less than</ng-container> <b>100</b>
          </mat-error>
          <mat-error *ngIf="item['controls'].amount.touched && item.hasError('positive', ['amount'])">
            <ng-container i18n>Amount should be greater than</ng-container> <b>0</b>
          </mat-error>
        </mat-form-field2>
        <mat-form-field2 class="mat-block">
          <mat-select formControlName="type" placeholder="Type" i18n-placeholder>
            <mat-option value="percent" i18n>Percent</mat-option>
            <mat-option value="flat" i18n>Flat</mat-option>
          </mat-select>
        </mat-form-field2>
        <mat-form-field2 class="mat-block">
          <mat-select formControlName="operation" placeholder="Operation" i18n-placeholder>
            <mat-option value="add" i18n>Add</mat-option>
            <mat-option value="subtract" i18n>Subtract</mat-option>
          </mat-select>
        </mat-form-field2>
      </div>
    </div>
    <div>
      <button (click)="addFormula()" class="btn btn-secondary-info w-100" type="button">
        <i class="far fa-plus"></i> <ng-container i18n>New Item</ng-container>
      </button>
    </div>
  </form>
</div>
<div class="modal-footer">
  <button (click)="close()" type="button" class="btn btn-light">
    <i class="far fa-times"></i> <ng-container i18n>Cancel</ng-container>
  </button>
  <button (click)="onSave(form)" type="button" class="btn btn-secondary-info create-btn">
    <i class="cs-icon cs-icon-save"></i> <ng-container i18n>save</ng-container>
  </button>
</div>
